/**
 * Crowd noise, synthesised. No audio files: a cheer is filtered noise with an
 * envelope plus a scatter of claps, which costs nothing to download and cannot
 * carry a licence.
 *
 * Browsers refuse audio until a gesture, so `unlock()` is called from the PLAY tap.
 */
export class CrowdAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambience: AudioBufferSourceNode | null = null;
  private ambienceGain: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private muted = false;

  /** Must be called from inside a user gesture handler. */
  unlock(): void {
    if (this.context) {
      void this.context.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    try {
      this.context = new Ctor();
    } catch {
      return; // no audio on this device; the game is fully playable silent
    }
    this.master = this.context.createGain();
    this.master.gain.value = this.muted ? 0 : 0.9;
    this.master.connect(this.context.destination);
    this.noise = this.createNoiseBuffer(this.context, 2.4);
    this.startAmbience();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(muted ? 0 : 0.9, this.context.currentTime, 0.05);
    }
  }

  /** A made basket: the crowd lifts, then settles. `intensity` 0..1. */
  cheer(intensity = 0.7): void {
    const ctx = this.context;
    if (!ctx || !this.master || !this.noise) return;
    const now = ctx.currentTime;
    const level = 0.22 + 0.5 * intensity;

    // The roar: noise through a moving band-pass, which is what a crowd sounds
    // like when a few hundred voices rise together.
    const source = ctx.createBufferSource();
    source.buffer = this.noise;
    source.loop = true;

    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.setValueAtTime(520, now);
    band.frequency.exponentialRampToValueAtTime(1250, now + 0.35);
    band.frequency.exponentialRampToValueAtTime(700, now + 1.5);
    band.Q.value = 0.9;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(level, now + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.6 + intensity * 0.6);

    source.connect(band).connect(gain).connect(this.master);
    source.start(now);
    source.stop(now + 2.4);

    // Applause: short bright bursts, thickening with intensity.
    const claps = Math.round(14 + intensity * 22);
    for (let i = 0; i < claps; i += 1) {
      this.clap(now + 0.05 + Math.random() * (0.9 + intensity * 0.5), 0.05 + Math.random() * 0.07);
    }

    // Ambience swells behind it for a moment.
    if (this.ambienceGain) {
      this.ambienceGain.gain.cancelScheduledValues(now);
      this.ambienceGain.gain.setValueAtTime(this.ambienceGain.gain.value, now);
      this.ambienceGain.gain.linearRampToValueAtTime(0.09 + intensity * 0.06, now + 0.2);
      this.ambienceGain.gain.linearRampToValueAtTime(0.045, now + 2.2);
    }
  }

  /** A referee's whistle: two tones a fifth apart with a fast warble, which is what
   *  gives a pea whistle its shrill trill. `count` blasts, spaced like a real call. */
  whistle(count = 1, startAt = 0): void {
    const ctx = this.context;
    if (!ctx || !this.master) return;
    const base = ctx.currentTime + startAt;
    for (let i = 0; i < count; i += 1) {
      const at = base + i * 0.42;
      const length = 0.3;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.1, at + 0.03);
      gain.gain.setValueAtTime(0.1, at + length - 0.08);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + length);
      gain.connect(this.master);

      // The warble: an LFO on the pitch of both tones.
      const warble = ctx.createOscillator();
      warble.type = 'sine';
      warble.frequency.value = 42;
      const warbleDepth = ctx.createGain();
      warbleDepth.gain.value = 90;
      warble.connect(warbleDepth);

      for (const freq of [2650, 3900]) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        warbleDepth.connect(osc.frequency);
        const voice = ctx.createGain();
        voice.gain.value = freq > 3000 ? 0.5 : 1;
        osc.connect(voice).connect(gain);
        osc.start(at);
        osc.stop(at + length + 0.05);
      }
      warble.start(at);
      warble.stop(at + length + 0.05);
    }
  }

  /** Time expired: one whistle, then the crowd deflating. */
  endLoss(): void {
    const ctx = this.context;
    if (!ctx || !this.master || !this.noise) return;
    this.whistle(1);
    const now = ctx.currentTime + 0.28;

    const source = ctx.createBufferSource();
    source.buffer = this.noise;
    source.loop = true;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    // Falling: a crowd that watched it slip away drops in pitch as it thins out.
    band.frequency.setValueAtTime(560, now);
    band.frequency.exponentialRampToValueAtTime(200, now + 1.8);
    band.Q.value = 1.2;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.2, now + 0.25);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.2);
    source.connect(band).connect(gain).connect(this.master);
    source.start(now);
    source.stop(now + 2.4);

    if (this.ambienceGain) {
      this.ambienceGain.gain.cancelScheduledValues(now);
      this.ambienceGain.gain.setValueAtTime(this.ambienceGain.gain.value, now);
      this.ambienceGain.gain.linearRampToValueAtTime(0.02, now + 1.6);
    }
  }

  /** Won it: two blasts, then the building comes down. */
  endWin(): void {
    this.whistle(2);
    this.cheer(1);
    window.setTimeout(() => this.cheer(0.85), 900);
  }

  /** End of a winning run: the buzzer, then the building coming down. */
  horn(): void {
    const ctx = this.context;
    if (!ctx || !this.master) return;
    const now = ctx.currentTime;
    for (const [freq, at, len] of [[262, 0, 0.5], [196, 0.12, 0.6]] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.16, now + at + 0.04);
      gain.gain.setValueAtTime(0.16, now + at + len - 0.12);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + len);
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 1200;
      osc.connect(lp).connect(gain).connect(this.master);
      osc.start(now + at);
      osc.stop(now + at + len + 0.05);
    }
    this.cheer(1);
  }

  /** The ball hitting iron: a short metallic knock the crowd draws breath over. */
  rattle(): void {
    const ctx = this.context;
    if (!ctx || !this.master) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(430, now);
    osc.frequency.exponentialRampToValueAtTime(280, now + 0.22);
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 900;
    band.Q.value = 3.4;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.14, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc.connect(band).connect(gain).connect(this.master);
    osc.start(now);
    osc.stop(now + 0.4);
  }

  /** A miss: a short disappointed murmur, quieter than a cheer. */
  groan(): void {
    const ctx = this.context;
    if (!ctx || !this.master || !this.noise) return;
    const now = ctx.currentTime;

    const source = ctx.createBufferSource();
    source.buffer = this.noise;
    source.loop = true;

    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.setValueAtTime(420, now);
    band.frequency.exponentialRampToValueAtTime(240, now + 0.7);
    band.Q.value = 1.1;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.14);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);

    source.connect(band).connect(gain).connect(this.master);
    source.start(now);
    source.stop(now + 1.1);
  }

  /** Countdown tick over the closing seconds. Low and quiet on purpose: a shrill
   *  beep at this point reads as an error, not as urgency. */
  tick(): void {
    const ctx = this.context;
    if (!ctx || !this.master) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(392, now);
    osc.frequency.exponentialRampToValueAtTime(330, now + 0.1);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1400;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.05, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    osc.connect(lp).connect(gain).connect(this.master);
    osc.start(now);
    osc.stop(now + 0.2);
  }

  /** The ball leaving the hand. */
  shoot(): void {
    const ctx = this.context;
    if (!ctx || !this.master) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(210, now);
    osc.frequency.exponentialRampToValueAtTime(90, now + 0.16);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.08, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc.connect(gain).connect(this.master);
    osc.start(now);
    osc.stop(now + 0.2);
  }

  dispose(): void {
    try {
      this.ambience?.stop();
      void this.context?.close();
    } catch {
      /* already closed */
    }
    this.context = null;
    this.master = null;
    this.ambience = null;
    this.ambienceGain = null;
  }

  private startAmbience(): void {
    const ctx = this.context;
    if (!ctx || !this.master || !this.noise) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noise;
    source.loop = true;

    const low = ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.value = 620;

    const gain = ctx.createGain();
    gain.gain.value = 0.045;

    source.connect(low).connect(gain).connect(this.master);
    source.start();
    this.ambience = source;
    this.ambienceGain = gain;
  }

  private clap(at: number, decay: number): void {
    const ctx = this.context;
    if (!ctx || !this.master || !this.noise) return;
    const source = ctx.createBufferSource();
    source.buffer = this.noise;
    source.loop = true;

    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1600 + Math.random() * 1800;
    band.Q.value = 1.6;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.05 + Math.random() * 0.05, at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + decay);

    source.connect(band).connect(gain).connect(this.master);
    source.start(at);
    source.stop(at + decay + 0.05);
  }

  private createNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
    const length = Math.floor(ctx.sampleRate * seconds);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < length; i += 1) {
      const white = Math.random() * 2 - 1;
      // A touch of brown noise underneath keeps it from hissing.
      last = (last + 0.02 * white) / 1.02;
      data[i] = white * 0.6 + last * 3.2;
    }
    return buffer;
  }
}
